import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

interface CountdownProps {
  targetDate?: Date;
  seconds?: number;
  onComplete?: () => void;
  showLabels?: boolean;
  format?: "blocks" | "compact";
  className?: string;
  style?: CSSProperties;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calc(end: Date): TimeLeft {
  const diff = Math.max(0, end.getTime() - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

export function Countdown({
  targetDate,
  seconds,
  onComplete,
  showLabels = true,
  format = "blocks",
  className,
  style,
}: CountdownProps) {
  const endRef = useRef<Date>(
    targetDate ?? new Date(Date.now() + (seconds ?? 60) * 1000)
  );

  const [time, setTime] = useState<TimeLeft>(() => calc(endRef.current));
  const doneRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      const t = calc(endRef.current);
      setTime(t);
      if (!doneRef.current && t.days === 0 && t.hours === 0 && t.minutes === 0 && t.seconds === 0) {
        doneRef.current = true;
        clearInterval(id);
        onComplete?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [onComplete]);

  const units = [
    { label: "Days", value: time.days },
    { label: "Hours", value: time.hours },
    { label: "Min", value: time.minutes },
    { label: "Sec", value: time.seconds },
  ];

  const showDays = time.days > 0;

  if (format === "compact") {
    return (
      <div className={cn("countdown-compact", className)} style={style}>
        {showDays && <span>{pad(time.days)}:</span>}
        <span>{pad(time.hours)}:</span>
        <span>{pad(time.minutes)}:</span>
        <span>{pad(time.seconds)}</span>
      </div>
    );
  }

  return (
    <div className={cn("countdown-blocks", className)} style={style}>
      {units.map(({ label, value }) => {
        if (label === "Days" && !showDays) return null;
        return (
          <div key={label} className="countdown-unit">
            <div className="countdown-value">{pad(value)}</div>
            {showLabels && <div className="countdown-label">{label}</div>}
          </div>
        );
      })}
    </div>
  );
}
